import type { CaptchaConfigPublic } from './captcha';

// First-party Pryma host suffixes. Mirrors FIRST_PARTY_CORS_DOMAINS on the
// API side. Used to disambiguate "page on a Pryma subdomain that happens to
// differ from the API's subdomain" (use the configured base) from "page on
// a customer's custom auth domain where the API serves the same host"
// (prefer same-origin).
const FIRST_PARTY_HOST_DOMAINS = ['pryma.dev', 'a5n.co'] as const;

export function isFirstPartyHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return FIRST_PARTY_HOST_DOMAINS.some(d => host === d || host.endsWith(`.${d}`));
}

// When auth-ui is served from a customer's custom auth domain (e.g.
// id.kezra.co), the CustomDomainResolver middleware on the API serves the
// same host. Routing API calls back to that same origin instead of the
// platform default keeps the OAuth callback URL on the customer's domain
// — otherwise the callback URL Google sees would be the platform host
// (auth.api.a5n.co), forcing every customer to register two redirect URIs
// in their IdP.
//
// But this same-origin fallback must NOT trigger when the page is on a
// Pryma-owned host that happens to differ from the API's host (e.g.
// id.pryma.dev page → auth.pryma.dev API). In that case the configured
// VITE_API_URL is right; falling back to same-origin would point requests
// at the auth-ui's nginx, which serves the SPA's index.html and the
// branding/captcha calls would silently land on HTML.
function resolveApiBase(): string {
  const configured = import.meta.env.VITE_API_URL ?? '';
  if (typeof window === 'undefined') return configured;
  if (!configured) return '';

  try {
    const apiHost = new URL(configured, window.location.origin).hostname;
    if (apiHost && apiHost !== window.location.hostname) {
      // Page on a first-party Pryma host → trust the configured API base.
      if (isFirstPartyHost(window.location.hostname)) return configured;
      // Page on a customer's custom domain → prefer same-origin so the API
      // is reached via CustomDomainResolver and OAuth callbacks stay on
      // the customer's host.
      return '';
    }
  } catch {
    /* malformed VITE_API_URL — fall through to the configured value */
  }
  return configured;
}

const API_BASE = resolveApiBase();

// Exposed so non-fetch callers (e.g. OAuth-start <a href> in LoginPage) build
// URLs against the same origin the rest of the app talks to.
export function getApiBase(): string {
  return API_BASE;
}

export type ApiResult<T = unknown> = {
  success: boolean;
  message?: string;
  error?: string;
  error_description?: string;
  data?: T;
  // Used by MFA challenge verify; the backend returns these at the top level
  // alongside `success: false` so the page can show remaining attempts.
  remaining_attempts?: number;
  // MFA challenge verify success returns these at the top level (no `data`).
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
  // Login endpoint returns mfa_required at the top level when MFA is required
  // for this user; the mfa_token + methods are nested under `data`.
  mfa_required?: boolean;
  user?: {
    id: number;
    email: string;
    name?: string;
    avatar_url?: string;
    mfa_enabled?: boolean;
  };
};

interface RequestOptions {
  token?: string;
}

async function request<T = unknown>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  options?: RequestOptions
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (options?.token) headers['Authorization'] = `Bearer ${options.token}`;

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 429) {
    return { success: false, error: 'Too many requests. Please wait a bit and try again.' };
  }

  let parsed: ApiResult<T>;
  try {
    parsed = (await response.json()) as ApiResult<T>;
  } catch {
    parsed = { success: response.ok, error: response.ok ? undefined : 'Request failed' };
  }

  // Some endpoints (notably the MFA challenge verify) return data at the top
  // level on success and omit the `success` flag. Fall back to the HTTP status
  // so callers can rely on `result.success` consistently.
  if (parsed.success === undefined) {
    parsed.success = response.ok;
  }

  return parsed;
}

function getJson<T = unknown>(path: string, options?: RequestOptions) {
  return request<T>('GET', path, undefined, options);
}

function postJson<T = unknown>(path: string, body: unknown, options?: RequestOptions) {
  return request<T>('POST', path, body, options);
}

function deleteJson<T = unknown>(path: string, options?: RequestOptions) {
  return request<T>('DELETE', path, undefined, options);
}

// ===== Branding (per-project, public) =====

export const BRANDING_COLOR_KEYS = [
  'primary_color',
  'primary_hover_color',
  'accent_color',
  'background_color',
  'foreground_color',
  'error_color',
] as const;

export type BrandingColorKey = (typeof BRANDING_COLOR_KEYS)[number];

export const OAUTH_PROVIDERS = ['google', 'github', 'microsoft', 'okta', 'apple'] as const;

export type OAuthProvider = (typeof OAUTH_PROVIDERS)[number];

export interface PublicAuthMethods {
  email_password: boolean;
  magic_link: boolean;
  email_otp: boolean;
  oauth_providers: OAuthProvider[];
}

export const BRANDING_LAYOUTS = ['centered', 'split'] as const;
export type BrandingLayout = (typeof BRANDING_LAYOUTS)[number];

export const BRANDING_THEMES = ['light', 'dark', 'auto'] as const;
export type BrandingTheme = (typeof BRANDING_THEMES)[number];

export interface BrandingPayload {
  logo_url: string | null;
  app_name: string | null;
  colors: Record<BrandingColorKey, string | null>;
  colors_dark: Record<BrandingColorKey, string | null>;
  layout: BrandingLayout;
  default_theme: BrandingTheme;
  allow_user_theme_override: boolean;
  auth_methods: PublicAuthMethods;
}

// Fetch the project's branding by client_id (= project UUID). Public,
// unauthenticated. Always resolves with a payload (even for unknown ids the
// API returns a default empty payload, never a 404), so callers can apply
// the same fallback logic uniformly.
export function getBranding(clientId: string) {
  return getJson<BrandingPayload>(`/api/auth/branding?client_id=${encodeURIComponent(clientId)}`);
}

// ===== CAPTCHA config (per-project, public) =====

// Fetch the project's CAPTCHA config (public attributes only — never
// the secret key). Returns the not-configured shape when the project
// has no row, so callers can use a single null-checking path.
export function getEndUserCaptchaConfig(projectId: string) {
  return getJson<CaptchaConfigPublic>(
    `/api/auth/projects/${encodeURIComponent(projectId)}/captcha-config`
  );
}

export function requestPasswordReset(email: string) {
  return postJson('/api/auth/forgot-password', { email });
}

export function resetPassword(params: {
  token: string;
  password: string;
  password_confirmation: string;
}) {
  return postJson('/api/auth/reset-password', params);
}

// ===== Hosted login (end-user) =====

// Path under which all end-user auth endpoints for a project live.
const endUserAuthPath = (projectId: string) => `/api/auth/projects/${projectId}`;

// When mfa_required is true on the response, the challenge details live
// under `data` (mfa_token, mfa_methods, expires_at). When false, the
// access_token/refresh_token live at the top level via ApiResult.
export interface EndUserMfaChallengeData {
  mfa_token: string;
  mfa_methods: string[];
  expires_at?: string;
}

export function endUserLogin(
  projectId: string,
  email: string,
  password: string,
  captchaToken?: string
) {
  const body: Record<string, string> = { email, password };
  if (captchaToken) body.captcha_token = captchaToken;
  return postJson<EndUserMfaChallengeData>(`${endUserAuthPath(projectId)}/login`, body);
}

export interface AuthorizeCodeResult {
  success: boolean;
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
}

// Authorize-code returns the plaintext code at the top level alongside the
// echoed state. We parse the raw response directly rather than going through
// ApiResult because the wire shape on success is `{ code, state }` (no `data`
// envelope) and on error is `{ success: false, error, error_description }`.
export async function authorizeCode(
  projectId: string,
  token: string,
  params: {
    redirect_uri: string;
    state?: string;
    code_challenge: string;
    code_challenge_method: string;
  }
): Promise<AuthorizeCodeResult> {
  const response = await fetch(`${API_BASE}${endUserAuthPath(projectId)}/authorize_code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });

  if (response.status === 429) {
    return { success: false, error: 'Too many requests. Please wait a bit and try again.' };
  }

  let body: Partial<AuthorizeCodeResult> & Record<string, unknown> = {};
  try {
    body = (await response.json()) as Partial<AuthorizeCodeResult>;
  } catch {
    return {
      success: false,
      error: response.ok ? undefined : 'Request failed',
    };
  }

  if (!response.ok) {
    return {
      success: false,
      error: typeof body.error === 'string' ? body.error : 'Request failed',
      error_description:
        typeof body.error_description === 'string' ? body.error_description : undefined,
    };
  }

  return {
    success: true,
    code: typeof body.code === 'string' ? body.code : undefined,
    state: typeof body.state === 'string' ? body.state : undefined,
  };
}

// ===== MFA =====

export type MfaMethod = 'totp' | 'recovery_code' | 'email';

export interface MfaStatusData {
  project_mfa_policy: 'disabled' | 'optional' | 'required';
  mfa_required: boolean;
  mfa_enabled: boolean;
  methods_allowed: string[];
  methods_enabled: string[];
  totp: {
    enabled: boolean;
    verified_at?: string | null;
    last_used_at?: string | null;
  };
}

export interface MfaSetupData {
  secret: string;
  qr_code_svg: string;
  qr_code_base64: string;
  uri: string;
  issuer: string;
  message: string;
}

export interface MfaVerifySetupData {
  message: string;
  recovery_codes: string[];
  recovery_codes_warning: string;
}

export interface RecoveryCodesData {
  total_codes: number;
  remaining_codes: number;
  used_codes: number;
}

export interface RegenerateRecoveryCodesData {
  recovery_codes: string[];
  warning: string;
}

const mfaPath = (projectId: string) => `/api/projects/${projectId}/auth/mfa`;

// Unauthenticated (uses mfa_token from URL during login challenge)
export function sendEmailMfaCode(projectId: string, mfaToken: string) {
  return postJson(`${mfaPath(projectId)}/email/send`, { mfa_token: mfaToken });
}

export function verifyMfaChallenge(
  projectId: string,
  params: { mfa_token: string; code: string; method: MfaMethod }
) {
  return postJson(`${mfaPath(projectId)}/verify`, params);
}

// Authenticated (needs end-user access token)
export function getMfaStatus(projectId: string, token: string) {
  return getJson<MfaStatusData>(`${mfaPath(projectId)}/status`, { token });
}

export function setupTotp(projectId: string, token: string) {
  return postJson<MfaSetupData>(`${mfaPath(projectId)}/totp/setup`, {}, { token });
}

export function verifyTotpSetup(projectId: string, token: string, code: string) {
  return postJson<MfaVerifySetupData>(`${mfaPath(projectId)}/totp/verify`, { code }, { token });
}

export function disableTotp(projectId: string, token: string) {
  return deleteJson(`${mfaPath(projectId)}/totp`, { token });
}

export function getRecoveryCodes(projectId: string, token: string) {
  return getJson<RecoveryCodesData>(`${mfaPath(projectId)}/recovery-codes`, { token });
}

export function regenerateRecoveryCodes(projectId: string, token: string) {
  return postJson<RegenerateRecoveryCodesData>(
    `${mfaPath(projectId)}/recovery-codes/regenerate`,
    {},
    { token }
  );
}
